import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { CloudArrowUpIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useMerchantFile } from '../../hooks/useMerchantFile';
import LeadNotesList from '../Leads/LeadNotesList';
import LeadCallHistory from '../Leads/LeadCallHistory';
import LoadingSpinner from '../UI/LoadingSpinner';
import {
  formatLeadStatusLabel,
  LEAD_STATUS_DESCRIPTIONS,
  leadStatusPillClass,
} from '../../constants/pipelineStatuses';
import { merchantFileService, MerchantFileData } from '../../services/smsService';
import { LeadStatus } from '../../types/lead';
import { cn } from '../../utils/cn';

interface MerchantFilePanelProps {
  leadId: string;
  leadName?: string;
  companyName?: string;
  onClose: () => void;
}

const PANEL_TRANSITION_MS = 320;

const CATEGORY_ICONS: Record<string, string> = {
  application: '📄',
  bank_statements: '🏦',
  identification: '🪪',
  voided_check: '🏦',
  financials: '📊',
  other: '📁',
};

const STATUS_BADGE: Record<string, string> = {
  uploaded: 'bg-lime-100 text-lime-700 dark:bg-lime-900/40 dark:text-lime-400',
  missing: 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400',
  pending: 'bg-slate-100 text-slate-500 dark:bg-muted dark:text-muted-foreground',
};

function formatShortDate(iso?: string | null) {
  if (!iso) return 'Pending';
  return new Date(iso).toLocaleDateString(undefined, { month: '2-digit', day: '2-digit' });
}

function UploadActionButton({
  onClick,
  compact = false,
  label = 'Upload',
}: {
  onClick: () => void;
  compact?: boolean;
  label?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={`${label} file`}
      aria-label={`${label} file`}
      className={`inline-flex items-center justify-center gap-1 rounded-lg border font-semibold text-primary shadow-sm transition-all active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
        compact
          ? 'border-primary/25 bg-primary/10 px-2 py-1 text-[9px] hover:border-primary/45 hover:bg-primary/20'
          : 'border-primary/30 bg-primary/10 px-2.5 py-1.5 text-[10px] hover:border-primary/50 hover:bg-primary/20 hover:shadow-md'
      }`}
    >
      <CloudArrowUpIcon className={compact ? 'h-3 w-3 shrink-0' : 'h-3.5 w-3.5 shrink-0'} />
      <span>{label}</span>
    </button>
  );
}

const MerchantFilePanel: React.FC<MerchantFilePanelProps> = ({
  leadId,
  leadName,
  companyName,
  onClose,
}) => {
  const {
    data,
    loading,
    reload,
    currentUserId,
    isAdmin,
    handleAddNote,
    handleUpdateNote,
    handleDeleteNote,
  } = useMerchantFile(leadId);
  const [callsOpen, setCallsOpen] = useState(true);
  const [copied, setCopied] = useState(false);
  const [panelVisible, setPanelVisible] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [uploadCategory, setUploadCategory] = useState<string | null>(null);
  const [uploadBankAccountId, setUploadBankAccountId] = useState<string | null>(null);

  const requestClose = useCallback(() => {
    setPanelVisible(false);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      onClose();
    }, PANEL_TRANSITION_MS);
  }, [onClose]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => setPanelVisible(true));
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    if (!panelVisible) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [panelVisible]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [requestClose]);

  useEffect(
    () => () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    },
    []
  );

  const application = data?.documents.application;
  const milestones = [
    { key: 'linkOpenedAt', label: 'Link Opened', value: application?.linkOpenedAt },
    { key: 'appDoneAt', label: 'App Done', value: application?.appDoneAt },
    { key: 'docsUploadedAt', label: 'Docs Uploaded', value: application?.docsUploadedAt },
    { key: 'completedAt', label: 'Completed', value: application?.completedAt },
  ];
  const completedCount = milestones.filter((m) => m.value).length;

  const handleCopyLink = async () => {
    if (!application?.applicationUrl) return;
    await navigator.clipboard.writeText(application.applicationUrl);
    setCopied(true);
    toast.success('Link copied');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleMilestoneToggle = async (key: string, current?: string | null) => {
    try {
      await merchantFileService.updateMilestones(leadId, {
        [key]: current ? null : new Date().toISOString(),
      } as any);
      await reload();
    } catch {
      toast.error('Failed to update milestone');
    }
  };

  const triggerUpload = (categorySlug: string, bankAccountId?: string) => {
    setUploadCategory(categorySlug);
    setUploadBankAccountId(bankAccountId || null);
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !uploadCategory) return;
    try {
      await merchantFileService.uploadDocument(leadId, uploadCategory, file, uploadBankAccountId || undefined);
      toast.success('File uploaded');
      await reload();
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploadCategory(null);
      setUploadBankAccountId(null);
    }
  };

  const status = data?.lead.status;
  const description =
    (status && LEAD_STATUS_DESCRIPTIONS[status as LeadStatus]) ||
    'Pipeline status for this merchant.';

  const panel = (
    <>
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/50 backdrop-blur-[2px] transition-opacity duration-300 ease-out',
          panelVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={requestClose}
        aria-hidden
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="merchant-file-panel-title"
        className={cn(
          'fixed top-0 right-0 z-50 flex h-full w-full max-w-lg flex-col border-l border-border bg-background shadow-2xl',
          'transform transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] will-change-transform',
          panelVisible ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileSelected} />

        <div className="flex items-center justify-between border-b border-border px-4 py-3 shrink-0">
          <div className="min-w-0">
            <h2 id="merchant-file-panel-title" className="font-semibold text-foreground">
              Merchant File
            </h2>
            <p className="text-sm text-muted-foreground truncate">
              {leadName || data?.lead.fullName || 'Merchant'}
              {(companyName || data?.lead.companyName) &&
                ` · ${companyName || data?.lead.companyName}`}
            </p>
          </div>
          <button
            type="button"
            onClick={requestClose}
            className="rounded-md p-1 transition-colors hover:bg-accent shrink-0"
            aria-label="Close sidebar"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto scroll-area px-4 py-4 pb-8 space-y-4">
          {loading && !data && (
            <div className="flex justify-center py-10">
              <LoadingSpinner size="md" />
            </div>
          )}

          {data && (
            <>
              <div className="bg-muted/60 rounded-2xl p-4">
                <h3 className="font-semibold text-sm mb-3 text-foreground">Application Status</h3>
                <span
                  className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${leadStatusPillClass(status)}`}
                >
                  {formatLeadStatusLabel(status)}
                  {data.lead.subStageName ? ` · ${data.lead.subStageName}` : ''}
                </span>
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{description}</p>
              </div>

              <div className="bg-muted/60 rounded-2xl p-4">
                <h3 className="font-semibold text-sm mb-3 text-foreground">Notes</h3>
                <LeadNotesList
                  notes={data.notes}
                  currentUserId={currentUserId}
                  isAdmin={isAdmin}
                  onAddNote={handleAddNote}
                  onUpdateNote={handleUpdateNote}
                  onDeleteNote={handleDeleteNote}
                />
              </div>

              <div className="bg-muted/60 rounded-2xl p-4">
                <button
                  type="button"
                  onClick={() => setCallsOpen((v) => !v)}
                  className="w-full flex justify-between items-center mb-3"
                >
                  <h3 className="font-semibold text-sm text-foreground">Call History</h3>
                  <span className="text-xs text-muted-foreground">{callsOpen ? '⌃' : '⌄'}</span>
                </button>
                {callsOpen && (
                  <LeadCallHistory
                    calls={data.calls}
                    leadName={leadName || data.lead.fullName}
                  />
                )}
              </div>

              <div className="bg-gradient-to-br from-card to-violet-500/5 rounded-2xl p-4 border border-violet-500/10">
                <div className="flex justify-between items-start mb-4 gap-2">
                  <div>
                    <h3 className="font-bold uppercase text-xs tracking-wide text-foreground">Documents</h3>
                    <p className="text-[10px] text-muted-foreground mt-1">Application link + file tracking</p>
                  </div>
                </div>

                {application?.applicationUrl && (
                  <div className="bg-card rounded-2xl p-4 border border-border mb-4">
                    <div className="flex gap-2 items-center mb-4">
                      <div className="truncate text-xs text-muted-foreground bg-muted rounded-full px-3 py-2 flex-1">
                        {application.applicationUrl}
                      </div>
                      <button
                        type="button"
                        onClick={handleCopyLink}
                        className="bg-primary text-primary-foreground rounded-full px-3 py-2 text-[10px] shrink-0"
                      >
                        {copied ? '✓' : 'Copy'}
                      </button>
                    </div>

                    <div className="relative px-1">
                      <div className="absolute left-4 right-4 top-[11px] h-1 bg-violet-500/20 rounded-full" />
                      <div
                        className="absolute left-4 top-[11px] h-1 bg-primary rounded-full transition-all"
                        style={{
                          right: `${Math.max(16, 100 - (completedCount / milestones.length) * 84)}px`,
                        }}
                      />
                      <div className="grid grid-cols-4 gap-2 relative z-10">
                        {milestones.map((step, index) => {
                          const done = Boolean(step.value);
                          return (
                            <button
                              key={step.key}
                              type="button"
                              onClick={() => handleMilestoneToggle(step.key, step.value)}
                              className="text-center"
                              title={`Toggle ${step.label}`}
                            >
                              <div
                                className={`mx-auto w-6 h-6 rounded-full flex items-center justify-center text-[10px] ${
                                  done ? 'bg-primary text-primary-foreground' : 'bg-violet-500/20 text-violet-600'
                                }`}
                              >
                                {done ? '✓' : index + 1}
                              </div>
                              <p className="text-[9px] text-violet-600 dark:text-violet-400 mt-2 leading-tight">
                                {step.label}
                              </p>
                              <p className="text-[8px] text-muted-foreground mt-1">
                                {formatShortDate(step.value)}
                              </p>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-3 text-xs">
                  {data.documents.categories.map((cat: MerchantFileData['documents']['categories'][number]) => {
                    const icon = CATEGORY_ICONS[cat.categorySlug] || '📁';
                    const isBank = cat.categorySlug === 'bank_statements';
                    return (
                      <div
                        key={cat.id}
                        className="bg-card rounded-2xl p-3 border border-border shadow-sm"
                      >
                        <div className="flex justify-between items-center mb-2">
                          <div>
                            <p className="font-semibold text-foreground">
                              {icon} {cat.label}
                            </p>
                            <p className="text-muted-foreground text-[10px]">
                              {cat.fileCount > 0 ? `${cat.fileCount} Files` : 'No file'}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span
                              className={`rounded-full px-2 py-1 text-[9px] font-semibold capitalize ${
                                STATUS_BADGE[cat.status] || STATUS_BADGE.pending
                              }`}
                            >
                              {cat.status}
                            </span>
                            <UploadActionButton onClick={() => triggerUpload(cat.categorySlug)} />
                          </div>
                        </div>

                        {isBank &&
                          data.documents.bankAccounts.map((acct: MerchantFileData['documents']['bankAccounts'][number]) => (
                            <div
                              key={acct.id}
                              className="bg-gradient-to-r from-violet-500/5 to-primary/5 rounded-xl p-3 mt-2 border border-violet-500/10"
                            >
                              <div className="flex justify-between items-center gap-2">
                                <div className="min-w-0">
                                  <p className="text-[10px] font-semibold text-violet-700 dark:text-violet-300 truncate">
                                    💳 {acct.label}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  <span
                                    className={`rounded-full px-2 py-1 text-[8px] capitalize ${
                                      STATUS_BADGE[acct.status] || STATUS_BADGE.pending
                                    }`}
                                  >
                                    {acct.status}
                                  </span>
                                  <UploadActionButton
                                    compact
                                    onClick={() => triggerUpload('bank_statements', acct.id)}
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </aside>
    </>
  );

  return createPortal(panel, document.body);
};

export default MerchantFilePanel;
